import * as React from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import { Link } from 'react-router-dom';
import EditIcon from '@material-ui/icons/Input';
import { withPreferences } from 'hcla-web-frontend-primitives/app/config/PreferencesProvider';

import { Theme, Typography, WithStyles } from '@material-ui/core';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles from '@material-ui/core/styles/withStyles';
import { mxEvent, mxEventObject } from 'mxgraph-js';
import HCLTreeMxGraph from '../../../../../mxGraph/HCLTreeMxGraph/HCLTreeMxGraph';
import FaultTreeMxGraph from '../../../../../mxGraph/HCLTreeMxGraph/FaultTreeMxGraph';
import EventTreeMxGraph from '../../../../../mxGraph/HCLTreeMxGraph/EventTreeMxGraph';
import BayesianNetworkMxGraph from '../../../../../mxGraph/HCLTreeMxGraph/BayesianNetworkMxGraph';

import { TreeNames, TreeTypeDictionary, TreeTypes } from '../Tools/TreeDictionaries';
import { FaultTreeMxGraphViewJSON } from './FaultTreeMxGraphEditorView';
import { EventTreeMxGraphViewJSON } from './EventTreeMxGraphEditorView';
import { BayesianNetworkMxGraphViewJSON } from './BayesianNetworkMxGraphEditorView';
import HCLTreeVertex from '../../../../../mxGraph/HCLTreeNode/HCLTreeVertex';
import { HCLTreeMxGraphViewJSON } from './HCLTreeMxGraphView';
import { HCLTreeMxGraphViewProps } from './HCLTreeMxGraphViewProps';
import { NodeLabelJSON } from '../../../QuantificationResult/NodeLabel/NodeLabel';

const styles = (theme: Theme) => createStyles({

  root: {
    display: 'flex',
    height: '100%',
    flexGrow: 1,
    overflow: 'auto',
    color: theme.palette.text.primary,
    fontFamily: 'Roboto Condensed',
    '&:active': {
      cursor: 'grabbing',
    },
  },

  tools: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    bottom: '4rem',
    right: '1rem',
  },

  iconButton: {
    display: 'block',
    color: theme.palette.text.primary,
  },

  graphRoot: {
    width: '100%',
    height: '100%',
    color: theme.palette.text.primary,
    fontFamily: 'Roboto Condensed',
    outline: 'none',
    '&:active': {
      cursor: 'grabbing',
    },
  },

  noNodesMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'center',
  },
});

export interface Props extends WithStyles<typeof styles>, HCLTreeMxGraphViewProps {
  data: HCLTreeMxGraphViewJSON | FaultTreeMxGraphViewJSON | EventTreeMxGraphViewJSON | BayesianNetworkMxGraphViewJSON;
  basePath: string;
  onVertexClick?: (vertex: HCLTreeVertex) => void;
  nodeLabel?: NodeLabelJSON;
}

/**
 * You can pick a cell
 */
class HCLTreeMxGraphVertexPickerView extends React.Component<Props> {
  private static GRAPH_OFFSET_X = 100000;

  private static GRAPH_OFFSET_Y = 100000;

  private graph: HCLTreeMxGraph;

  private graphContainer: React.RefObject<HTMLScriptElement | HTMLDivElement>; // div ref to mount graph onto

  private rootRef: React.RefObject<HTMLScriptElement | HTMLDivElement>;

  /**
   * @param {Props} props
   * @throws Error if tree name is not one of {@link TreeNames}
   */
  constructor(props: Props) {
    super(props);

    const { data, provider } = this.props;

    this.graphContainer = React.createRef();
    this.rootRef = React.createRef();
    // @ts-ignore
    this.graph = this.instantiateGraph(data.tree_type as TreeTypes);
    this.graph.setPreferencesProvider(provider);

    const treeData = ['basic_events', 'branches', 'sequences', 'functional_events', 'components', 'gates', 'house_events', 'bayesian_nodes'];
    // @ts-ignore
    this.treeDataIsEmpty = treeData.every((key: string) => this.isEmpty(data.tree_data[key]));
  }

  private treeDataIsEmpty = true;

  private instantiateGraph(treeType: TreeTypes): FaultTreeMxGraph | EventTreeMxGraph | BayesianNetworkMxGraph {
    switch (treeType) {
      case TreeTypes.FAULT_TREE:
        return new FaultTreeMxGraph(this.onGraphMounted, this.onGraphUnmounted);
      case TreeTypes.EVENT_TREE:
        return new EventTreeMxGraph(this.onGraphMounted, this.onGraphUnmounted);
      case TreeTypes.BAYESIAN_NETWORK:
        return new BayesianNetworkMxGraph(this.onGraphMounted, this.onGraphUnmounted);
      default:
        throw new Error(`Fatal Error: Tree Name ${treeType} is not supported`);
    }
  }

  private addEventListeners = () => {
    const { onVertexClick } = this.props;
    this.graph.addListener(mxEvent.CLICK, (sender: any, event: mxEventObject) => {
      const targetCell = event.getProperty('cell');
      if (targetCell instanceof HCLTreeVertex) onVertexClick(targetCell);
    });
  };

  private onGraphMounted = () => {
    const { data, nodeLabel } = this.props;

    // const selectedVertex = this.graph.getVertices().filter(vertex => vertex.getKey() === nodeLabel.node_id)[0];
    this.graph.populateFromJSON(data.tree_data);
    this.graph.applyDefaultLayout();
    this.graph.setPanning(true);
    // this.graph.getSelectionModel().setCell(selectedVertex);
    this.graph.setCellsSelectable(true);
    this.graph.setConnectable(false);
    this.graph.setCellsLocked(true);
    this.addEventListeners();
  };

  private onGraphUnmounted = () => { };


  private handleZoomIn = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    this.graph.zoomIn();
  };

  private handleZoomOut = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    this.graph.zoomOut();
  };

  componentDidMount(): void {
    if (!this.treeDataIsEmpty) {
      this.graph.mount(this.graphContainer.current as HTMLScriptElement);
      this.setGraphOffsetWithinView(HCLTreeMxGraphVertexPickerView.GRAPH_OFFSET_X, HCLTreeMxGraphVertexPickerView.GRAPH_OFFSET_Y);
    }
  }

  private setGraphOffsetWithinView = (dx: number, dy: number) => {
    this.graph.getVertices().forEach((vertex) => {
      const geometry = vertex.getGeometry();
      if (geometry.relative) return;
      geometry.x += dx;
      geometry.y += dy;
    });

    this.graph.view.setTranslate(-dx + this.rootRef.current.offsetWidth / 2 - this.graph.getGraphBounds().width / 2, -dy + 50); // center x
    this.graph.view.refresh();
  };

  componentWillUnmount(): void {
    this.graph.unmount();
  }

  isEmpty = (obj: Record<string, any>) => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) return false;
    }
    return true;
  };

  render() {
    const { classes, data, basePath } = this.props;

    let view = null;

    if (this.treeDataIsEmpty) {
      view = (
        <div className={classes.noNodesMessage}>
          <Typography variant="h4" gutterBottom color="textPrimary">Referenced Tree is Empty</Typography>
          <Typography variant="subtitle1" color="textSecondary">Open in editor to start adding nodes.</Typography>
        </div>
      );
    } else {
      view = (
        <div className={classes.graphRoot} ref={this.graphContainer as React.RefObject<HTMLDivElement>} />
      );
    }

    return (
      <div className={classes.root} ref={this.rootRef as React.RefObject<HTMLDivElement>}>
        {view}
        <div className={classes.tools}>
          <Tooltip placement="right" title="open in editor">
            <Link to={`${basePath}/${data.model.id}/${TreeTypeDictionary[data.tree_type as TreeTypes]}/${data.id}`} target="_blank">
              <IconButton className={classes.iconButton}>
                <EditIcon />
              </IconButton>
            </Link>
          </Tooltip>
          <Tooltip title="Zoom in" placement="right">
            <IconButton className={classes.iconButton} onClick={this.handleZoomIn}>
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom out" placement="right">
            <IconButton className={classes.iconButton} onClick={this.handleZoomOut}>
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(withPreferences(HCLTreeMxGraphVertexPickerView));
